import React, { useEffect, useRef, useState, useMemo } from "react";
import SVGraphics from "../../../assets/SVGraphics";
import dicomParser from "dicom-parser";
import { useViewer } from "../DicomViewerNew/context/ViewerContext";
const DicomSlices = (props) => {
  const sliceRefs = useRef([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [dicomMetadata, setDicomMetadata] = useState(null);
  const totalGroups = props.data.nodules.length;

  const { zoom, setParsedDicomData } = useViewer();

  const handleGroupToggle = (groupIndex) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupIndex]: !prev[groupIndex],
    }));
  };

  const sortedNodulesWithGrains = useMemo(() => {
    if (!props.data.nodules || !props.data.grains) return [];
    const unsorted = props.data.nodules.map((nodule) =>
      props.data.grains.find((g) => g.id == nodule.grain_id)
    );
    return unsorted.filter(Boolean).sort((a, b) => a.slices.min - b.slices.min);
  }, [props.data.nodules, props.data.grains]);

  // Extract DICOM metadata
  useEffect(() => {
    if (!props.examData?.dicomFiles?.length) return;

    const extractMetadata = async () => {
      try {
        const firstFile = props.examData.dicomFiles[0];
        const buffer = new Uint8Array(await firstFile.arrayBuffer());
        const ds = dicomParser.parseDicom(buffer);

        const metadata = {
          // Existing fields
          patientID: ds.string("x00100020") || null,
          studyDate: ds.string("x00080020") || null,
          patientPosition: ds.string("x00185100") || null,
          manufacturer: ds.string("x00080070") || null,
          manufacturerModel: ds.string("x00081090") || null,
          stationName: ds.string("x00081010") || null,
          kvp: ds.string("x00180060") || null,
          tubeCurrent: ds.string("x00181151") || null,
          sliceThickness: ds.string("x00180050") || null,
          sliceLocation: ds.string("x00201041") || null,
          rows: ds.uint16("x00280010") || null,
          columns: ds.uint16("x00280011") || null,
          pixelSpacing: ds.string("x00280030") || null,
          windowCenter: ds.string("x00281050") || null,
          windowWidth: ds.string("x00281051") || null,
          seriesNumber: ds.string("x00200011") || null,
          instanceNumber: ds.string("x00200013") || null,
          numberOfFrames: ds.string("x00280008") || null,
          reconstructionDiameter: ds.string("x00181100") || null,
          convolutionKernel: ds.string("x00181210") || null,
          spiralPitchFactor: ds.string("x00189311") || null,
          tableSpeed: ds.string("x00189309") || null,

          // 🔽 Additional fields for report
          patientName: ds.string("x00100010") || null,
          patientSex: ds.string("x00100040") || null,
          patientBirthDate: ds.string("x00100030") || null,
          referringPhysician: ds.string("x00080090") || null,
          accessionNumber: ds.string("x00080050") || null,
          modality: ds.string("x00080060") || null,
          studyDescription: ds.string("x00081030") || null,
          institutionName: ds.string("x00080080") || null,
          institutionAddress: ds.string("x00080081") || null,
        };

        // Format DICOM-style dates to DD/MM/YYYY
        const formatDate = (rawDate) => {
          if (rawDate?.length === 8) {
            const y = rawDate.substring(0, 4);
            const m = rawDate.substring(4, 6);
            const d = rawDate.substring(6, 8);
            return `${d}/${m}/${y}`;
          }
          return null;
        };

        metadata.formattedDate = formatDate(metadata.studyDate);
        metadata.formattedBirthDate = formatDate(metadata.patientBirthDate);

        setDicomMetadata(metadata);
        setParsedDicomData(metadata);
      } catch (error) {
        console.error("Error extracting DICOM metadata:", error);
      }
    };

    extractMetadata();
  }, [props.examData]);

  // Auto-expand the active grain
  useEffect(() => {
    if (props.currentGrainId != null && sortedNodulesWithGrains.length > 0) {
      const gi = sortedNodulesWithGrains.findIndex(
        (g) => g.id === props.currentGrainId
      );
      if (gi >= 0) {
        setExpandedGroups((prev) => ({ ...prev, [gi]: true }));
      }
    }
  }, [props.currentGrainId, sortedNodulesWithGrains]);

  const groupedSlices = Array.from({ length: totalGroups }, (_, groupIndex) => {
    const grain = sortedNodulesWithGrains[groupIndex];
    if (!grain) return null;

    const start = grain.slices.min;
    const end = grain.slices.max;
    const groupSlices = props.imageIds.slice(start - 1, end);
    const groupGrainId = grain.id;

    return (
      <div
        key={groupIndex}
        ref={(el) => (sliceRefs.current[groupIndex] = el)}
        data-group-index={groupIndex}
        className="w-50 text-white">
        <div
          className="flex items-center h-[35px] py-[5px] pl-[12px] cursor-pointer"
          onClick={() => handleGroupToggle(groupIndex)}>
          <SVGraphics svgname="rightChevron" width="4px" height="7px" />
          <div className="rounded-full flex justify-center ml-[14px] mr-[10px] items-center bg-[#5A3EFF] w-4 h-4 text-[11px]">
            {groupIndex + 1}
          </div>
          Slices {start} - {end}
        </div>

        {expandedGroups[groupIndex] && (
          <div className="bg-[#5A3EFF33] rounded-[8px] mx-[10px] mt-[4px] pb-[4px]">
            {groupSlices.map((_, idx) => {
              const sliceIndex = start + idx - 1;
              const isSelected = sliceIndex === props.currentImageIndex;
              const hasMeasurements =
                props.annotationsData?.[sliceIndex]?.length > 0;
              const hasFindings = props.data?.nodules?.some((nodule) => {
                const g = props.data.grains?.find(
                  (gr) => gr.id === nodule.grain_id
                );
                return g?.main_slice === sliceIndex + 1;
              });
              const hasComments = hasMeasurements || hasFindings;

              return (
                <div
                  key={sliceIndex}
                  onClick={() =>
                    props.handleSliceClick(sliceIndex, groupGrainId)
                  }
                  className={`cursor-pointer text-white flex items-center justify-between h-[35px] rounded-[8px] mx-[6px] px-[14px] mt-[4px] ${
                    isSelected ? "bg-[#5A3EFF]" : "bg-transparent"
                  }`}>
                  <span className="leading-none flex items-center text-[15px]">
                    Slice {sliceIndex + 1}
                  </span>
                  {hasComments && (
                    <div className="flex items-center mt-2 justify-center w-[26px] h-[26px]">
                      <SVGraphics
                        svgname="NoteOnSlice"
                        className="text-white opacity-80 w-[26px] h-[26px]"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="w-60 flex flex-col flex-shrink-0 h-full bg-[black]">
      {/* Header Section */}
      <div className="py-5 pl-6 font-Inter text-white grid gap-2 border-y border-[#2B2B2B]">
        <h4 className="text-base bold">Genesis Findings</h4>
        <h4 className="text-sm bold-medium text-[#C1C1C1]">
          {props.data.nodules.length} Nodules were detected
        </h4>
      </div>

      {/* Scrollable Slices Section */}
      <div className="flex-1 overflow-y-auto py-5 pl-6">{groupedSlices}</div>

      {/* Slice Info Section (fixed at bottom, doesn't shrink) */}
      <div className="text-white text-sm  border-t border-[#2B2B2B] mt-auto mb-[px] shrink-0">
        <div className="flex items-center px-4 py-3 border-b border-[#2B2B2B]">
          <div className="w-4 h-4 rounded-full border border-white flex items-center justify-center mr-2">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
          <span className="text-sm font-medium">Slice Info</span>
        </div>

        <div className="mt-3 space-y-2 text-xs text-[#C1C1C1] px-4 pb-2 mb-[40px]">
          <div>{dicomMetadata?.patientID || "UNKNOWN"}</div>
          <div>{dicomMetadata?.manufacturerModel || "UNKNOWN"}</div>
          <div>{dicomMetadata?.formattedDate || "UNKNOWN"}</div>
          <div>{dicomMetadata?.patientPosition || "UNKNOWN"}</div>
          <div>L (Left)</div>
          <div>A (Anterior)</div>

          {/* Gap after A (Anterior) */}
          <div className="h-2"></div>

          <div>P (Posterior)</div>
          <div>
            ST:{" "}
            {dicomMetadata?.sliceThickness
              ? `${dicomMetadata.sliceThickness} mm`
              : "UNKNOWN"}{" "}
            SL:{" "}
            {dicomMetadata?.sliceLocation
              ? `${dicomMetadata.sliceLocation} mm`
              : "UNKNOWN"}
          </div>
          <div>I: {dicomMetadata?.convolutionKernel || "UNKNOWN"}</div>
          <div>Series: {dicomMetadata?.seriesNumber || "UNKNOWN"}</div>

          {/* Gap after Series */}
          <div className="h-2"></div>

          <div>
            {dicomMetadata?.tubeCurrent
              ? `${dicomMetadata.tubeCurrent} mA`
              : "UNKNOWN"}{" "}
            {dicomMetadata?.kvp ? `${dicomMetadata.kvp} kV` : "UNKNOWN"}
          </div>
          <div>Zoom: {zoom ? zoom : "UNKNOWN"}</div>
          <div>
            WL: {dicomMetadata?.windowCenter || "UNKNOWN"} WW:{" "}
            {dicomMetadata?.windowWidth || "UNKNOWN"}
          </div>
          <div>
            Images: {props.currentImageIndex + 1}/{props.imageIds.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DicomSlices;
